import constVars from '~/constants/constVars';
import React, { ReactElement, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import * as userActions from '~/store/actions/userActions';
import * as loansActions from '~/store/actions/loansActions';
import { useAuth } from '~/contexts/AuthProvider';
import { Route, RouteProps } from 'react-router';
import Spinner from '~/components/shared/Spinner/SpinnerCircular';
import { pushToGtmAction } from '~/store/actions/gtmActions';
import { pushToSentryAction } from '~/store/actions/sentryActions';
import {
  defaultLanguageToMarket,
  setLanguage,
  setMarket,
} from './store/slices/intl.slice';
import { formatPersonalNumber } from './helpers/formatters.helper';
import { RootState } from './store/types/sharedTypes';
import { theme } from './styles/themes';
import { getAuth0Audience } from './helpers/utils';
import { CountryCode, GlobalErrorType, StepStatus } from '~/enums';
import { isTokenSet } from './store/slices/globalError.slice';
import posthog from 'posthog-js';
import { TOKEN, accessToken } from '~/store/apis/utils/accessToken';

const ProtectedRoute = ({ ...props }: RouteProps): ReactElement => {
  /* Selectors */
  const { overview } = useSelector((state: RootState) => state.loans);
  const location = useLocation();
  const history = useHistory();
  const { info: userInfo, userIsSet, companies } = useSelector(
    (state: RootState) => state.user
  );
  const { errorType } = useSelector((state: RootState) => state.globalError);

  /* Other hooks */
  const {
    getAccessTokenSilently,
    user,
    isAuthenticated,
    isLoading,
    isLoggingOut,
    provider,
  } = useAuth();
  const dispatch = useDispatch();

  // Handle authentication redirect for non-internal-prod environments
  useEffect(() => {
    if (
      !isLoading &&
      !isAuthenticated &&
      !isLoggingOut &&
      import.meta.env.MODE !== 'internal-prod'
    ) {
      if (window.location.pathname !== constVars.ROUTE_DEFAULT) {
        sessionStorage.setItem(
          'returnTo',
          `${window.location.pathname}${window.location.search}`
        );
      }

      // Redirect to login page so users can select their market and login method
      history.push(`/${constVars.ROUTE_LOGIN}`);
    }
  }, [isLoading, isAuthenticated, isLoggingOut, history]);

  /* Gets access token and saves it in local storage */
  const setAccessToken = async () => {
    // For Auth0, pass the audience parameter. For Cognito, it's not needed.
    const options =
      provider === 'auth0'
        ? {
            authorizationParams: {
              audience: getAuth0Audience(),
            },
          }
        : undefined;

    const _accessToken = await getAccessTokenSilently(options);
    if (!_accessToken) {
      throw new Error(`accessToken is invalid, it is ${_accessToken}`);
    }

    dispatch(isTokenSet(true));
  };

  let personalNumber =
    user && (user[constVars.AUTH_USER_PERSONAL_NUMBER] as string);
  const market = user && user[constVars.AUTH_MARKET];
  const email = user?.[constVars.AUTH_USER_EMAIL];
  const userCode = user && (user['https://onboarding/userCode'] as string);
  const clientAccesses = user && (user['custom:client_accesses'] as number[]);

  // If market is DK format Personal ID (xxxxxx-xxxx)
  if (personalNumber && market && market.toUpperCase() === CountryCode.DK) {
    personalNumber = formatPersonalNumber(personalNumber, market);
  }

  useEffect(() => {
    if (market) {
      window.__QRED_MARKET__ = market;
      dispatch(setMarket(market));
      if (userIsSet) {
        userInfo.language
          ? dispatch(setLanguage(userInfo.language))
          : dispatch(defaultLanguageToMarket(market));
      }
    }
  }, [market, userIsSet]);

  /* Effects */
  useEffect(() => {
    // Fetch user data if we have:
    // 1. Personal number (Auth0 or Cognito with SSN)
    // 2. NL market with email
    // 3. Cognito user with userCode (for markets without SSN in token)
    const shouldFetchUserData =
      personalNumber ||
      (market === CountryCode.NL && email) ||
      (provider === 'cognito' && userCode);

    if (shouldFetchUserData) {
      console.log('[ProtectedRoute] Fetching user data', {
        provider,
        personalNumber,
        userCode,
        clientAccesses,
        market,
      });

      setAccessToken()
        .then(() => {
          if (!userInfo.personalNumber && personalNumber) {
            dispatch(userActions.updateUserPersonalNumber(personalNumber));
          }
          if (!location.pathname.includes(constVars.ROUTE_ONBOARDING_OFFER)) {
            dispatch(userActions.fetchUserData());
          }
        })
        .catch((err) => {
          dispatch(pushToSentryAction(err, 'Problem getting accessToken'));
        });
    }
  }, [personalNumber, userCode, provider]);

  useEffect(() => {
    if (companies.length > 0) {
      dispatch(
        pushToGtmAction({
          eventName: 'main',
          actionName: 'user_login',
          stepStatus: StepStatus.Started,
          stepActionNumber: 1.1,
        })
      );
    }
  }, [companies]);

  useEffect(() => {
    if (isAuthenticated) {
      accessToken.getDecoded().then((decodedToken: any) => {
        const tokenUserCode = decodedToken[TOKEN.USER_CODE];

        if (tokenUserCode) {
          posthog.identify(tokenUserCode);
          Sentry.setUser({ id: tokenUserCode });
        }
      });
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (overview.length) {
      overview.map((loan) =>
        dispatch(
          loansActions.fetchTerminatedContractById(loan.id, loan.companyId)
        )
      );
    }
  }, [overview.length]);

  // Show spinner while loading authentication state
  if (isLoading) {
    return <Spinner color={theme.colors.secondaryGray} />;
  }

  // If not authenticated and not in internal-prod, the useEffect above will handle redirect
  // In internal-prod mode, we allow access even if not authenticated
  if (!isAuthenticated && import.meta.env.MODE !== 'internal-prod') {
    return <Spinner color={theme.colors.secondaryGray} />;
  }

  if (
    userInfo.language ||
    errorType === GlobalErrorType.USER_HAS_NO_ACCOUNT ||
    errorType === GlobalErrorType.API_FAILURE ||
    errorType === GlobalErrorType.USER_SIGNED_OFFER_AND_NO_ACCOUNT ||
    location.pathname.includes(constVars.ROUTE_ONBOARDING_OFFER)
  ) {
    return <Route {...props} />;
  }
  return <Spinner color={theme.colors.secondaryGray} />;
};

export default ProtectedRoute;
